import { cn } from "../../lib/utils";

const base =
  "relative inline-flex box-border max-w-full shrink-0 cursor-pointer select-none items-center justify-center whitespace-nowrap rounded-md border border-transparent text-sm font-medium leading-sm outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&>span]:min-w-0 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:first-child:not(:last-child)]:mr-1 [&_svg:last-child:not(:first-child)]:ml-0.5 [&_svg:not([class*='size-'])]:size-4";

const variants = {
  default:
    "border-border-primary-always-black bg-bg-accent-primary text-text-always-white hover:brightness-[.92] active:brightness-[.85] data-[pressed]:brightness-[.85]",
  neutral:
    "border-border-primary-always-black bg-bg-accent-neutral text-text-primary-inverse hover:brightness-[.92] active:brightness-[.85] data-[pressed]:brightness-[.85]",
  outline:
    "border-border-primary bg-bg-elevated-transparent text-text-primary hover:bg-[linear-gradient(var(--color-tint-tertiary),var(--color-tint-tertiary)),linear-gradient(var(--color-bg-elevated-transparent),var(--color-bg-elevated-transparent))] data-[pressed]:bg-[linear-gradient(var(--color-tint-tertiary),var(--color-tint-tertiary)),linear-gradient(var(--color-bg-elevated-transparent),var(--color-bg-elevated-transparent))]",
  secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
  subtle:
    "border-input bg-accent text-accent-foreground hover:bg-foreground/10",
  ghost:
    "bg-transparent text-text-secondary hover:bg-tint-secondary hover:text-text-primary active:bg-tint-secondary active:text-text-primary data-[pressed]:bg-tint-secondary data-[pressed]:text-text-primary",
  destructive:
    "border-border-primary bg-bg-elevated-transparent text-text-destructive hover:bg-[linear-gradient(var(--color-tint-tertiary),var(--color-tint-tertiary)),linear-gradient(var(--color-bg-elevated-transparent),var(--color-bg-elevated-transparent))]",
  link: "bg-transparent text-text-accent-primary underline-offset-4 hover:bg-transparent hover:text-text-accent-primary/80 hover:no-underline",
  inline:
    "border-0 bg-transparent text-text-primary hover:bg-tint-secondary active:bg-tint-secondary data-[pressed]:bg-tint-secondary data-[popup-open]:bg-tint-secondary",
} satisfies Record<NonNullable<ButtonVariantProps["variant"]>, string>;

const sizes = {
  default:
    "h-7 gap-1 px-2.5 [font-size:var(--text-nav)] [line-height:var(--line-heights-nav)] [&_svg:not([class*='size-'])]:size-4",
  xs: "h-5 gap-1 px-1.5 [font-size:var(--text-caption)] [line-height:var(--line-heights-caption)] [&_svg:not([class*='size-'])]:size-3",
  sm: "h-6 gap-1 px-2 text-xs leading-xs [&_svg:not([class*='size-'])]:size-3.5",
  provider:
    "h-7 w-full gap-1 px-2.5 [font-size:var(--text-nav)] [line-height:var(--line-heights-nav)] [&_svg:not([class*='size-'])]:size-4",
  lg: "h-9 gap-1.5 px-3 text-sm leading-sm [&_svg:not([class*='size-'])]:size-4.5",
  icon: "size-8 [&_svg:not([class*='size-'])]:size-4",
  "icon-3xs": "size-5 [&_svg:not([class*='size-'])]:size-3",
  "icon-2xs": "size-6 [&_svg:not([class*='size-'])]:size-3.5",
  "icon-xs": "size-7 [&_svg:not([class*='size-'])]:size-4",
  "icon-sm": "size-8 [&_svg:not([class*='size-'])]:size-4",
  "icon-lg": "size-9 [&_svg:not([class*='size-'])]:size-4.5",
  link: "h-auto gap-1 p-0 text-sm leading-sm",
  inline: "h-6 gap-1 px-1.5 text-xs leading-xs",
} satisfies Record<NonNullable<ButtonVariantProps["size"]>, string>;

const radii = {
  default: null,
  none: "rounded-none",
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  full: "rounded-full",
} satisfies Record<NonNullable<ButtonVariantProps["radius"]>, string | null>;

const layouts = {
  default: null,
  fit: "w-fit",
  full: "w-full",
  "full-justify": "w-full justify-between",
  "full-center-with-start-icon":
    "w-full justify-center [&_svg:first-child]:absolute [&_svg:first-child]:left-2",
} satisfies Record<NonNullable<ButtonVariantProps["layout"]>, string | null>;

function buttonVariants({
  variant = "default",
  size = "default",
  radius = "default",
  layout = "default",
}: ButtonVariantProps = {}) {
  return cn(
    base,
    variants[variant],
    sizes[size],
    radii[radius],
    layouts[layout],
  );
}

type ButtonVariantProps = {
  variant?:
    | "default"
    | "neutral"
    | "outline"
    | "secondary"
    | "subtle"
    | "ghost"
    | "destructive"
    | "link"
    | "inline";
  size?:
    | "default"
    | "xs"
    | "sm"
    | "provider"
    | "lg"
    | "icon"
    | "icon-3xs"
    | "icon-2xs"
    | "icon-xs"
    | "icon-sm"
    | "icon-lg"
    | "link"
    | "inline";
  radius?: "default" | "none" | "sm" | "md" | "lg" | "full";
  layout?:
    | "default"
    | "fit"
    | "full"
    | "full-justify"
    | "full-center-with-start-icon";
};

export { buttonVariants, type ButtonVariantProps };
